import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IconType } from "react-icons";
import {
  FiAlertTriangle, FiBox, FiCheckCircle, FiChevronDown, FiClock, FiGlobe, FiGrid, FiHelpCircle, FiLayers,
  FiMaximize, FiMinus, FiPlus, FiRadio, FiRefreshCw, FiRotateCcw, FiSearch, FiShuffle, FiSliders, FiX, FiXOctagon,
} from "react-icons/fi";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Live } from "@/components/Topbar";
import {
  clearNetPositions,
  createNetLink,
  deleteNetLink,
  getNetMap,
  getOverview,
  graphTargets,
  linkGraph,
  portGraph,
  listNetLinks,
  listNetNodes,
  listNetPositions,
  saveNetPositions,
  updateNetLink,
  OVERVIEW_REFRESH_MS,
  type GraphTargetDevice,
  type GraphTargetPort,
  type GraphWindow,
  type NetLinkForm,
  type NetPosition,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GraphImg } from "@/components/GraphImg";
import { formatBps } from "@/lib/format";
import { clientToUser, pixelsToUser, userScale, type Box } from "@/lib/viewbox";
import { applySaved, layout, NODE_H, NODE_W, type Point } from "@/lib/netlayout";
import {
  adjacency, disambiguate, edgeKey, fitBox, focusBox, impactOf, inferRoles, linkState, midTrim, nodeState, pathToCore,
  type CorePath, type LinkState, type NodeState, type Role,
} from "@/lib/netgraph";
import { incidentTitle } from "@/lib/incident";
import { flowDirection, flowDuration } from "@/lib/netflow";
import { fmtNum, tStatus, useI18n, type Key } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import type { Alert, NetLink, NetMapEdge, NetMapNode, NetMapResponse, Overview } from "@/lib/types";

// A gesture that moved further than this is a pan/drag, not a click.
const CLICK_SLOP = 3;
const REFRESH_MS = 30_000;
// The four ranges the operator asked for on the map popups.
const WINDOWS: GraphWindow[] = ["6h", "24h", "7d", "30d"];


/** Add / edit / delete an operator-drawn link. Admin only; the caller gates it. */
function LinkDialog({ edit, onClose }: { edit: NetLink | null; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: nodeData } = useQuery({ queryKey: ["net-nodes"], queryFn: listNetNodes });
  const options = nodeData?.items ?? [];
  const [form, setForm] = useState<NetLinkForm>({
    source_node: edit?.source_node ?? "",
    target_node: edit?.target_node ?? "",
    source_port_id: edit?.source_port_id ?? null,
    target_port_id: edit?.target_port_id ?? null,
    label: edit?.label ?? "",
  });

  const done = () => {
    qc.invalidateQueries({ queryKey: ["netmap"] });
    qc.invalidateQueries({ queryKey: ["net-links"] });
    onClose();
  };
  const save = useMutation({
    mutationFn: async (body: NetLinkForm) => {
      const payload = { ...body, label: body.label?.trim() || null };
      if (edit) await updateNetLink(edit.id, payload);
      else await createNetLink(payload);
    },
    onSuccess: done,
  });
  const del = useMutation({ mutationFn: () => deleteNetLink(edit!.id), onSuccess: done });

  const picker = (side: "source" | "target") => {
    const nodeKey = side === "source" ? form.source_node : form.target_node;
    const portKey = side === "source" ? "source_port_id" : "target_port_id";
    const ports = options.find((o) => o.key === nodeKey)?.ports ?? [];
    const port = form[portKey];
    return (
      <div className="grid gap-2">
        <label className="block text-xs text-slate-400">
          {t(side === "source" ? "netmap.aSide" : "netmap.bSide")}
          <Select
            value={nodeKey || undefined}
            onValueChange={(v) =>
              setForm({ ...form, [side === "source" ? "source_node" : "target_node"]: v, [portKey]: null })
            }
          >
            <SelectTrigger className="mt-1 w-full text-xs">
              <SelectValue placeholder={t("netmap.pickDevice")} />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.key} value={o.key}>
                  {o.name} {o.kind === "olt" ? "(OLT)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="block text-xs text-slate-400">
          {t("netmap.portOptional")}
          <Select
            value={port === null || port === undefined ? "none" : String(port)}
            onValueChange={(v) => setForm({ ...form, [portKey]: v === "none" ? null : Number(v) })}
          >
            <SelectTrigger className="mt-1 w-full text-xs" disabled={ports.length === 0}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("netmap.noPort")}</SelectItem>
              {ports.map((pt) => (
                <SelectItem key={pt.id} value={String(pt.id)}>
                  {pt.name}
                  {pt.speed_bps ? ` · ${formatBps(pt.speed_bps)}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>
    );
  };

  const valid =
    form.source_node !== "" && form.target_node !== "" && form.source_node !== form.target_node;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t(edit ? "netmap.editLink" : "netmap.addLink")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          {picker("source")}
          {picker("target")}
          <label className="block text-xs text-slate-400">
            {t("netmap.labelOptional")}
            <Input
              value={form.label ?? ""}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={t("netmap.labelPlaceholder")}
            />
          </label>
        </div>
        {!valid && form.source_node !== "" && form.source_node === form.target_node && (
          <p className="text-sm text-amber-300">{t("netmap.needTwo")}</p>
        )}
        {(save.error || del.error) && (
          <p className="text-sm text-signal-crit">{((save.error ?? del.error) as Error).message}</p>
        )}
        <DialogFooter>
          {edit && (
            <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
              {t(del.isPending ? "alerts.deleting" : "common.delete")}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={!valid || save.isPending} onClick={() => save.mutate(form)}>
            {t(save.isPending ? "common.saving" : "common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Ranges({ win, onChange }: { win: GraphWindow; onChange: (w: GraphWindow) => void }) {
  return (
    <div className="flex items-center gap-1">
      {WINDOWS.map((w) => (
        <Button key={w} size="sm" variant={w === win ? "default" : "secondary"} onClick={() => onChange(w)}>
          {w}
        </Button>
      ))}
    </div>
  );
}

/** One port's graph, opened from a row of the device popup. */
function PortGraphDialog({ device, port, onClose }: { device: GraphTargetDevice; port: GraphTargetPort; onClose: () => void }) {
  const { t } = useI18n();
  const [win, setWin] = useState<GraphWindow>("24h");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{device.name} · {port.ifname}</DialogTitle>
          <DialogDescription>{port.ifalias ?? t("common.noDescription")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-400">
          <span>Rx <span className="font-mono text-slate-200">{formatBps(port.rx_bps)}</span></span>
          <span>Tx <span className="font-mono text-slate-200">{formatBps(port.tx_bps)}</span></span>
          <span>{t("netmap.speed")} <span className="font-mono text-slate-200">{formatBps(port.speed_bps)}</span></span>
          <span>{port.oper_status ? tStatus(t, port.oper_status) : "—"}</span>
          <div className="ml-auto"><Ranges win={win} onChange={setWin} /></div>
        </div>
        <GraphImg
          queryKey={["graph", device.device_id, port.port_id, win]}
          fetch={(w) => portGraph(device.device_id, port.port_id, win, w)}
          alt={t("graphs.alt", { device: device.name, port: port.ifname, win })}
        />
      </DialogContent>
    </Dialog>
  );
}

/** Item 2: click a device → modal listing all its ports; a row opens its graph. */
function DevicePortsDialog({ node, onClose }: { node: NetMapNode; onClose: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const foreign = node.kind === "neighbour";
  // Same key as the Graphs page with an empty search, so one fetch serves both
  // and a click on another device does not refetch (staleTime).
  const targets = useQuery({
    queryKey: ["graph-targets", ""],
    queryFn: () => graphTargets(""),
    enabled: !foreign,
    staleTime: 60_000,
  });
  // Ports arrive in the server's natural ifName order (natLess in graph.go),
  // the same order the Graphs page shows — nothing to re-sort here.
  const device = targets.data?.devices.find((d) => d.device_id === node.librenms_id);
  const [port, setPort] = useState<GraphTargetPort | null>(null);

  let body: React.ReactNode;
  if (foreign) {
    body = (
      <p className="text-sm text-slate-400">
        {t("netmap.peerNoPorts", { name: node.name, proto: node.protocol ?? "LLDP/CDP" })}
      </p>
    );
  } else if (targets.isPending) {
    body = <p className="text-sm text-slate-400">{t("netmap.loadingPorts")}</p>;
  } else if (targets.error) {
    body = <p className="text-sm text-rose-300">{(targets.error as Error).message}</p>;
  } else if (!targets.data!.librenms_configured) {
    body = <p className="text-sm text-amber-300">{t("common.librenmsNotConfigured")}</p>;
  } else if (!device) {
    body = <p className="text-sm text-amber-300">{t("netmap.notMirrored")}</p>;
  } else if (device.ports.length === 0) {
    body = <p className="text-sm text-slate-400">{t("netmap.noPortsMirrored")}</p>;
  } else {
    body = (
      <div className="max-h-[60vh] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("netmap.portsName")}</TableHead>
              <TableHead>{t("netmap.portsAlias")}</TableHead>
              <TableHead>{t("netmap.speed")}</TableHead>
              <TableHead>{t("netmap.portsUpDown")}</TableHead>
              <TableHead>{t("netmap.portsRx")}</TableHead>
              <TableHead>{t("netmap.portsTx")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {device.ports.map((p) => (
              <TableRow key={p.port_id} className="cursor-pointer" onClick={() => setPort(p)} title={t("netmap.rowTitle")}>
                <TableCell className="font-mono">{p.ifname}</TableCell>
                <TableCell className="max-w-64 truncate text-slate-400">{p.ifalias ?? ""}</TableCell>
                <TableCell className="font-mono">{formatBps(p.speed_bps)}</TableCell>
                <TableCell>{p.oper_status ? tStatus(t, p.oper_status) : "—"}</TableCell>
                <TableCell className="font-mono">{formatBps(p.rx_bps)}</TableCell>
                <TableCell className="font-mono">{formatBps(p.tx_bps)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{node.name}</DialogTitle>
          <DialogDescription className="font-mono">{node.hostname}</DialogDescription>
        </DialogHeader>
        {body}
        {node.kind === "olt" && node.olt_id !== null && (
          <DialogFooter>
            <Button size="sm" variant="secondary" onClick={() => navigate(`/olts/${node.olt_id}`)}>{t("netmap.openOlt")}</Button>
          </DialogFooter>
        )}
        {device && port && <PortGraphDialog device={device} port={port} onClose={() => setPort(null)} />}
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Visual vocabulary. Colour is never the only carrier: node status also has a
// rail, an icon and a word; link state also has thickness, dash and a ✕ badge.

const C = {
  // Healthy is deliberately calm: on a quiet network nothing should shout.
  good: "#3d8a6f",
  warn: "var(--color-signal-warn)",
  crit: "var(--color-signal-crit)",
  none: "#475569",
  signal: "var(--color-signal)",
  ink: "#0b1220",
};
type Shown = NodeState | "stale";
const NODE_COLOR: Record<Shown, string> = { up: C.good, warn: C.warn, down: C.crit, unknown: "#64748b", stale: "#64748b" };
const STATE_ICON: Record<Shown, IconType> = {
  up: FiCheckCircle, warn: FiAlertTriangle, down: FiXOctagon, unknown: FiHelpCircle, stale: FiClock,
};
const LINK_COLOR: Record<LinkState, string> = { healthy: C.good, moderate: C.warn, high: C.crit, down: C.crit, nodata: C.none };
const LINK_STATES: LinkState[] = ["healthy", "moderate", "high", "down", "nodata"];

const ROLES: Role[] = ["core", "agg", "router", "switch", "olt", "device", "foreign"];
const ROLE_ICON: Record<Role, IconType> = {
  core: FiGlobe, agg: FiLayers, router: FiShuffle, switch: FiGrid, olt: FiRadio, device: FiBox, foreign: FiHelpCircle,
};
const ROLE_TAG: Record<Role, string> = {
  core: "CORE", agg: "AGG", router: "RTR", switch: "SW", olt: "OLT", device: "DEV", foreign: "LLDP",
};
const NODE_STATES: NodeState[] = ["up", "warn", "down", "unknown"];

/** Zoom-dependent detail: far = structure only, near = interfaces and traffic. */
type Lod = "far" | "mid" | "near";
const lodOf = (s: number): Lod => (s < 0.4 ? "far" : s < 0.7 ? "mid" : "near");

const pct = (u: number | null | undefined) => (u === null || u === undefined ? "—" : `${u.toFixed(0)}%`);
const share = (bps: number | null, speed: number | null) => (bps === null || !speed ? null : (bps / speed) * 100);
const short = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function uptime(s: number | null): string {
  if (s === null) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

// ---------------------------------------------------------------------------
// View settings: layers, labels, filters. Per browser, survives reloads.

type Settings = {
  monitored: boolean; manual: boolean; discovered: boolean; peers: boolean;
  names: boolean; ips: boolean; ifaces: boolean; capacity: boolean; rates: boolean;
  util: boolean; flow: boolean;
  hideStatus: NodeState[]; hideRoles: Role[];
};
// Discovered links off by default: they duplicate the operator's own links.
const DEFAULTS: Settings = {
  monitored: true, manual: true, discovered: false, peers: false,
  names: true, ips: false, ifaces: true, capacity: false, rates: false,
  // Traffic flow on, as it always was, unless the OS asks for reduced motion.
  util: true, flow: !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  hideStatus: [], hideRoles: [],
};
const SETTINGS_KEY = "nocops.netmap.view";
/** Node keys the operator marked as core; overrides the inferred core. */
const CORES_KEY = "nocops.netmap.cores";

function useSettings() {
  const [s, setS] = useState<Settings>(() => {
    try {
      return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}") };
    } catch {
      return DEFAULTS;
    }
  });
  useEffect(() => localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)), [s]);
  return [s, (p: Partial<Settings>) => setS((o) => ({ ...o, ...p }))] as const;
}

/** Everything the inspectors need to reason about the graph. */
type Graph = {
  byId: Map<number, NetMapNode>;
  edges: NetMapEdge[];
  roles: Map<number, Role>;
  states: Map<number, NodeState>;
  cores: Set<number>;
  adj: ReturnType<typeof adjacency>;
  ends: Map<string, [number, number]>;
  olts: Overview["olts"];
  alerts: Alert[];
};

type Sel = { kind: "node"; id: number } | { kind: "edge"; key: string } | null;
type Failure = { node?: number; edge?: string };

// ---------------------------------------------------------------------------
// Small UI atoms shared by the panels.

function Eyebrow({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <span className="eyebrow">{children}</span>
      {aside && <span className="text-[10.5px] text-slate-500">{aside}</span>}
    </div>
  );
}

function StateBadge({ state, big }: { state: Shown | LinkState; big?: boolean }) {
  const { t } = useI18n();
  const node = state in NODE_COLOR;
  const color = node ? NODE_COLOR[state as Shown] : LINK_COLOR[state as LinkState];
  const Icon = node ? STATE_ICON[state as Shown] : state === "down" ? FiXOctagon : state === "nodata" ? FiHelpCircle : FiCheckCircle;
  const label = node ? t(`lm.st.${state}` as Key) : t(`lm.link.${state}` as Key);
  return (
    <span className={cn("inline-flex items-center gap-1.5 font-medium", big ? "text-sm" : "text-xs")} style={{ color }}>
      <Icon className={big ? "size-4" : "size-3.5"} /> {label}
    </span>
  );
}

function Meter({ value }: { value: number | null }) {
  const tone = value === null ? C.none : value > 80 ? C.crit : value >= 50 ? C.warn : C.good;
  return (
    <span className="block h-1 w-full overflow-hidden rounded-full bg-rule">
      <span className="block h-full rounded-full" style={{ width: `${Math.min(100, value ?? 0)}%`, background: tone }} />
    </span>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="data min-w-0 truncate text-right text-[12.5px] text-slate-200">{children}</span>
    </div>
  );
}

function Check({ on, onChange, children }: { on: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 py-1 text-[12.5px] text-slate-300 hover:text-slate-100">
      <input type="checkbox" className="size-3.5 accent-cyan-400" checked={on} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Failure impact: what loses its path to core. Inferred from topology, so it
// is split into what is observed down (confirmed) and what merely could be.

function ImpactBlock({
  failed, down, g, showing, onShow, onPick,
}: {
  failed: Failure; down: boolean; g: Graph; showing: boolean; onShow: () => void; onPick: (id: number) => void;
}) {
  const { t, locale } = useI18n();
  const hit = useMemo(() => impactOf(failed, g.cores, g.adj), [failed, g.cores, g.adj]);
  if (g.cores.size === 0) return <p className="text-xs text-slate-500">{t("lm.noCore")}</p>;

  const confirmed = [...hit].filter((id) => g.states.get(id) === "down");
  const potential = [...hit].filter((id) => g.states.get(id) !== "down");
  // A failing OLT takes its own ONTs down with it, so it counts itself.
  const oltIds = [...hit, ...(failed.node !== undefined ? [failed.node] : [])]
    .map((id) => g.byId.get(id))
    .filter((n): n is NetMapNode => !!n && n.kind === "olt" && n.olt_id !== null)
    .map((n) => n.olt_id!);
  const olts = g.olts.filter((o) => oltIds.includes(o.id));
  const onts = olts.reduce((a, o) => a + o.onts_total, 0);
  const online = olts.reduce((a, o) => a + o.onts_online, 0);
  const alerts = g.alerts.filter((a) => a.target_type === "olt" && oltIds.includes(a.target_id));

  const names = (ids: number[], tone: string, hollow: boolean) => (
    <ul className="mt-1 space-y-0.5">
      {ids.slice(0, 6).map((id) => (
        <li key={id}>
          <button type="button" onClick={() => onPick(id)} className="flex items-center gap-2 text-left text-[12.5px] text-slate-300 hover:text-signal">
            <span className="size-2 shrink-0 rounded-full" style={hollow ? { boxShadow: `inset 0 0 0 1.5px ${tone}` } : { background: tone }} />
            {g.byId.get(id)?.name}
          </button>
        </li>
      ))}
      {ids.length > 6 && <li className="pl-4 text-[11px] text-slate-500">+{ids.length - 6}</li>}
    </ul>
  );

  return (
    <section className={cn("rounded-md border p-3", down ? "border-signal-crit/40 bg-signal-crit/[0.06]" : "border-rule")}>
      <Eyebrow aside={t("lm.impactInferred")}>{t(down ? "lm.impactPotential" : "lm.impactIfFails")}</Eyebrow>
      {hit.size === 0 && olts.length === 0 ? (
        <p className="text-xs text-slate-400">{t("lm.impactNone")}</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Figure n={hit.size} label={t("lm.impactDevices")} />
            <Figure n={olts.length} label="OLT" />
            <Figure n={onts} label={t("lm.impactOnts")} hint={olts.length ? t("lm.impactOntsOnline", { n: fmtNum(locale, online) }) : undefined} />
          </div>
          {confirmed.length > 0 && (
            <div className="mt-3">
              <span className="eyebrow text-signal-crit">{t("lm.confirmed")} · {confirmed.length}</span>
              {names(confirmed, C.crit, false)}
            </div>
          )}
          {potential.length > 0 && (
            <div className="mt-3">
              <span className="eyebrow text-signal-warn">{t("lm.potential")} · {potential.length}</span>
              {names(potential, C.warn, true)}
            </div>
          )}
          {alerts.length > 0 && (
            <div className="mt-3">
              <span className="eyebrow">{t("lm.relatedAlerts")}</span>
              <ul className="mt-1 space-y-0.5">
                {alerts.map((a) => (
                  <li key={a.id}>
                    <Link to="/alerts" className="data text-[12px] text-slate-300 hover:text-signal">
                      #{a.id} · {incidentTitle(t, a)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Button size="xs" variant={showing ? "default" : "outline"} className="mt-3" onClick={onShow}>
            {t("lm.showImpact")}
          </Button>
        </>
      )}
    </section>
  );
}

function Figure({ n, label, hint }: { n: number; label: string; hint?: string }) {
  return (
    <div>
      <div className="data text-xl leading-none text-slate-100">{n}</div>
      <div className="mt-1 text-[11px] text-slate-500">{label}</div>
      {hint && <div className="text-[10.5px] text-slate-600">{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inspectors: the right-hand drawer. Clicking the map never navigates away.

function Drawer({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <aside className="absolute inset-y-0 right-0 z-20 flex w-[min(380px,100%)] flex-col border-l border-rule bg-panel/95 shadow-2xl shadow-black/50 backdrop-blur">
      <header className="flex h-11 shrink-0 items-center justify-between border-b border-rule px-4">
        <span className="eyebrow">{title}</span>
        <Button variant="ghost" size="icon-xs" onClick={onClose} aria-label={t("lm.close")}><FiX /></Button>
      </header>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">{children}</div>
    </aside>
  );
}

function LinkInspector({
  edge, g, link, admin, impactShown, onImpact, onPick, onEdit, onClose,
}: {
  edge: NetMapEdge; g: Graph; link: NetLink | null; admin: boolean; impactShown: boolean;
  onImpact: () => void; onPick: (id: number) => void; onEdit: (l: NetLink) => void; onClose: () => void;
}) {
  const { t } = useI18n();
  const [win, setWin] = useState<GraphWindow>("24h");
  const ls = linkState(edge);
  const a = g.byId.get(edge.source), b = g.byId.get(edge.target);
  const end = (n: NetMapNode | undefined, port: string | null) => (
    <button type="button" onClick={() => n && onPick(n.id)} className="group block w-full text-left">
      <span className="block truncate text-[15px] font-semibold text-slate-100 group-hover:text-signal">{n?.name ?? "?"}</span>
      <span className="data block truncate text-xs text-slate-400">{port ?? "—"}</span>
    </button>
  );
  const rx = share(edge.rx_bps, edge.speed_bps), tx = share(edge.tx_bps, edge.speed_bps);
  return (
    <Drawer title={t("lm.linkDetails")} onClose={onClose}>
      <section className="space-y-2">
        {end(a, edge.local_port)}
        <div className="flex items-center gap-2 pl-1 text-xs text-slate-500">
          <span className="text-base leading-none">↕</span>
          <span className="data text-slate-300">{formatBps(edge.speed_bps)}</span>
          {edge.label && <span className="truncate">· {edge.label}</span>}
        </div>
        {end(b, edge.remote_port)}
      </section>

      <section>
        <Eyebrow aside={t(edge.origin === "manual" ? "lm.originManual" : "lm.originDiscovered")}>{t("lm.status")}</Eyebrow>
        <StateBadge state={ls} big />
      </section>

      <section>
        <Eyebrow aside={`${t("lm.capacity")} ${formatBps(edge.speed_bps)}`}>{t("lm.traffic")}</Eyebrow>
        {[["RX", edge.rx_bps, rx], ["TX", edge.tx_bps, tx]].map(([k, v, u]) => (
          <div key={k as string} className="mb-2.5">
            <div className="mb-1 flex items-baseline justify-between">
              <span className="eyebrow">{k as string}</span>
              <span className="data text-sm text-slate-100">
                {formatBps(v as number | null)} <span className="text-slate-500">{pct(u as number | null)}</span>
              </span>
            </div>
            <Meter value={u as number | null} />
          </div>
        ))}
        <Row label={t("lm.utilization")}>{pct(edge.util_pct)}</Row>
        {edge.traffic_port_id && edge.traffic_device_id && (
          <Link to={`/graphs?device=${edge.traffic_device_id}&port=${edge.traffic_port_id}`} className="mt-1 inline-flex text-[12px] text-signal hover:underline">
            {t("lm.viewTraffic")} →
          </Link>
        )}
      </section>

      {(ls === "down" || g.cores.size > 0) && (
        <ImpactBlock
          failed={{ edge: edgeKey(edge) }} down={ls === "down"} g={g}
          showing={impactShown} onShow={onImpact} onPick={onPick}
        />
      )}

      <section>
        <Eyebrow aside={link ? <Ranges win={win} onChange={setWin} /> : undefined}>{t("lm.graph")}</Eyebrow>
        {link === null ? (
          <p className="text-xs text-slate-500">{t("netmap.discoveredNoGraph")}</p>
        ) : (
          <div className="space-y-3">
            {(["source", "target"] as const).map((side) => {
              const pinned = side === "source" ? link.source_port_id != null : link.target_port_id != null;
              const dev = (side === "source" ? a : b)?.name ?? side;
              const port = side === "source" ? edge.local_port : edge.remote_port;
              return (
                <div key={side} className="min-w-0 space-y-1">
                  <p className="text-[11px] text-slate-400">{dev} <span className="data text-slate-300">{port ?? ""}</span></p>
                  {pinned ? (
                    <GraphImg
                      queryKey={["link-graph", link.id, side, win]}
                      fetch={(w) => linkGraph(link.id, side, win, w)}
                      alt={t("graphs.alt", { device: dev, port: port ?? side, win })}
                    />
                  ) : (
                    <p className="text-xs text-amber-300">{t("netmap.noPortPinned")}{admin ? t("netmap.noPortPinnedAdmin") : ""}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {admin && link && (
        <Button size="sm" variant="secondary" onClick={() => onEdit(link)}>{t("lm.edit")}</Button>
      )}
    </Drawer>
  );
}

/** Node names along a path, starting at `from`. */
function pathNames(from: number, keys: string[], g: Graph): string[] {
  const out = [g.byId.get(from)?.name ?? "?"];
  let at = from;
  for (const k of keys) {
    const [s, d] = g.ends.get(k) ?? [at, at];
    at = s === at ? d : s;
    out.push(g.byId.get(at)?.name ?? "?");
  }
  return out;
}

function DeviceInspector({
  node, g, path, tracing, impactShown, pinned, onTrace, onImpact, onPick, onPickEdge, onPorts, onPin, onClose,
}: {
  node: NetMapNode; g: Graph; path: CorePath | null; tracing: boolean; impactShown: boolean; pinned: boolean;
  onTrace: () => void; onImpact: () => void; onPick: (id: number) => void; onPickEdge: (k: string) => void;
  onPorts: () => void; onPin: () => void; onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const role = g.roles.get(node.id)!;
  const state = g.states.get(node.id)!;
  const foreign = role === "foreign";
  const Icon = ROLE_ICON[role];
  const links = g.edges.filter((e) => e.source === node.id || e.target === node.id);
  const olt = node.olt_id !== null ? g.olts.find((o) => o.id === node.olt_id) : undefined;
  const alerts = node.olt_id !== null ? g.alerts.filter((a) => a.target_type === "olt" && a.target_id === node.olt_id) : [];

  return (
    <Drawer title={t("lm.deviceDetails")} onClose={onClose}>
      <section>
        <div className="flex items-start gap-3">
          <span className={cn("grid size-9 shrink-0 place-items-center rounded-md border", foreign ? "border-dashed border-slate-600 text-slate-500" : "border-rule bg-white/[0.03] text-slate-300")}>
            <Icon className="size-4" />
          </span>
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-semibold text-slate-100">{node.name}</h3>
            <p className="mt-0.5 text-[11.5px] text-slate-400">
              {t(`lm.role.${role}` as Key)}
              {role !== "olt" && role !== "foreign" && (
                <span className="text-slate-600"> · {t(pinned ? "lm.pinned" : "lm.inferred")}</span>
              )}
            </p>
          </div>
        </div>
        <div className="mt-3"><StateBadge state={state} big /></div>
      </section>

      {foreign ? (
        <p className="rounded-md border border-dashed border-slate-700 p-3 text-xs text-slate-400">
          {t("lm.foreignNote", { proto: node.protocol ?? "LLDP/CDP" })}
        </p>
      ) : (
        <section className="divide-y divide-rule/60">
          <Row label={t("lm.mgmt")}>{node.hostname}</Row>
          <Row label={t("lm.platform")}>{[node.os, node.hardware].filter(Boolean).join(" · ") || "—"}</Row>
          <Row label={t("lm.uptime")}>{uptime(node.uptime_s)}</Row>
          {(node.cpu_pct !== null || node.mem_pct !== null) && (
            <Row label={t("lm.load")}>{pct(node.cpu_pct)} / {pct(node.mem_pct)}</Row>
          )}
          {olt && <Row label={t("lm.impactOnts")}>{fmtNum(locale, olt.onts_online)} / {fmtNum(locale, olt.onts_total)}</Row>}
        </section>
      )}

      {alerts.length > 0 && (
        <section>
          <Eyebrow>{t("lm.alerts")}</Eyebrow>
          <ul className="space-y-1">
            {alerts.map((a) => (
              <li key={a.id} className="flex items-center gap-2 text-[12.5px]">
                <span className="size-2 rounded-full" style={{ background: a.severity === "critical" ? C.crit : C.warn }} />
                <Link to="/alerts" className="text-slate-300 hover:text-signal">{incidentTitle(t, a)}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <Eyebrow aside={links.length}>{t("lm.links")}</Eyebrow>
        {links.length === 0 && <p className="text-xs text-slate-500">{t("lm.noLinksHere")}</p>}
        <ul className="divide-y divide-rule/60">
          {links.map((e) => {
            const out = e.source === node.id;
            const peer = g.byId.get(out ? e.target : e.source);
            const ls = linkState(e);
            return (
              <li key={edgeKey(e)}>
                <button type="button" onClick={() => onPickEdge(edgeKey(e))} className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-2 py-1.5 text-left hover:bg-white/[0.02]">
                  <span className="size-2 rounded-full" style={{ background: LINK_COLOR[ls] }} title={t(`lm.link.${ls}` as Key)} />
                  <span className="min-w-0">
                    <span className="block truncate text-[12.5px] text-slate-200">{peer?.name ?? "?"}</span>
                    <span className="data block truncate text-[11px] text-slate-500">
                      {(out ? e.local_port : e.remote_port) ?? "—"}
                      {e.origin === "discovered" && " · LLDP/CDP"}
                    </span>
                  </span>
                  <span className="data text-[12px]" style={{ color: ls === "nodata" ? "#64748b" : LINK_COLOR[ls] }}>
                    {ls === "down" ? t("lm.link.down") : pct(e.util_pct)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {!foreign && (
        <section>
          <Button size="sm" variant={tracing ? "default" : "outline"} onClick={onTrace} disabled={g.cores.size === 0}>
            <FiShuffle /> {t("lm.pathToCore")}
          </Button>
          {tracing && (
            <div className="mt-2 space-y-1.5 text-[12px]">
              {!path ? (
                <p className="text-slate-500">{t("lm.pathNone")}</p>
              ) : (
                <>
                  <p>
                    <span className="eyebrow mr-2 text-signal">{t("lm.pathPrimary")}</span>
                    <span className="text-slate-300">{pathNames(node.id, path.primary, g).join(" → ")}</span>
                    <span className="text-slate-500"> · {t("lm.hops", { n: path.primary.length })}</span>
                  </p>
                  {path.backup ? (
                    <p>
                      <span className="eyebrow mr-2 text-signal/70">{t("lm.pathBackup")}</span>
                      <span className="text-slate-300">{pathNames(node.id, path.backup, g).join(" → ")}</span>
                    </p>
                  ) : (
                    path.primary.length > 0 && <p className="text-signal-warn">{t("lm.pathSingle")}</p>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {!foreign && (
        <ImpactBlock
          failed={{ node: node.id }} down={state === "down"} g={g}
          showing={impactShown} onShow={onImpact} onPick={onPick}
        />
      )}

      <section className="flex flex-wrap gap-2">
        {!foreign && <Button size="xs" variant="secondary" onClick={onPorts}>{t("lm.viewPorts")}</Button>}
        {!foreign && (
          <Button size="xs" variant="secondary" onClick={onPin} title={t("lm.coreTitle")}>
            <FiGlobe /> {t(pinned ? "lm.unsetCore" : "lm.setCore")}
          </Button>
        )}
        {node.kind === "olt" && node.olt_id !== null && (
          <Button size="xs" variant="secondary" onClick={() => navigate(`/olts/${node.olt_id}`)}>{t("netmap.openOlt")}</Button>
        )}
        {!foreign && (
          <Button size="xs" variant="secondary" onClick={() => navigate(`/topology?locate=${encodeURIComponent(node.key)}`)}>{t("lm.viewTopology")}</Button>
        )}
        {!foreign && <Button size="xs" variant="secondary" onClick={() => navigate(node.librenms_id ? `/graphs?device=${node.librenms_id}` : "/graphs")}>{t("lm.trafficGraphs")}</Button>}
        <Button size="xs" variant="secondary" onClick={() => navigate("/alerts")}>{t("lm.alerts")}</Button>
      </section>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Toolbar pieces.

function Search({ nodes, edges, onPick }: { nodes: NetMapNode[]; edges: NetMapEdge[]; onPick: (n: NetMapNode) => void }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    const out: { node: NetMapNode; hint: string }[] = [];
    for (const n of nodes) {
      const f = [n.name, n.hostname, n.os, n.hardware].find((v) => v?.toLowerCase().includes(s));
      if (f) { out.push({ node: n, hint: f === n.name ? n.hostname : f }); continue; }
      // Interfaces: a port name belongs to the end it was reported on.
      const e = edges.find((e) =>
        (e.source === n.id && e.local_port?.toLowerCase().includes(s)) ||
        (e.target === n.id && e.remote_port?.toLowerCase().includes(s)));
      if (e) out.push({ node: n, hint: (e.source === n.id ? e.local_port : e.remote_port) ?? "" });
    }
    return out.slice(0, 8);
  }, [q, nodes, edges]);
  const pick = (n: NetMapNode) => { onPick(n); setQ(""); setOpen(false); };
  return (
    <div className="relative w-full max-w-72">
      <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
      <Input
        value={q}
        placeholder={t("lm.search")}
        className="h-8 border-rule bg-panel pl-8 text-[12.5px]"
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && hits[0]) pick(hits[0].node);
          if (e.key === "Escape") { setQ(""); setOpen(false); }
        }}
        aria-label={t("lm.search")}
      />
      {open && q.trim() && (
        <ul className="absolute top-9 z-30 w-full overflow-hidden rounded-md border border-rule bg-panel shadow-xl shadow-black/40">
          {hits.length === 0 && <li className="px-3 py-2 text-xs text-slate-500">{t("lm.noResults")}</li>}
          {hits.map((h) => (
            <li key={h.node.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(h.node)}
                className="flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left hover:bg-white/[0.04]"
              >
                <span className="truncate text-[12.5px] text-slate-200">{h.node.name}</span>
                <span className="data truncate text-[11px] text-slate-500">{h.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FiltersPanel({
  s, set, foreignCount, onClose,
}: {
  s: Settings; set: (p: Partial<Settings>) => void; foreignCount: number | null; onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const toggle = <T,>(list: T[], v: T, on: boolean) => (on ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <div className="absolute top-3 left-3 z-20 max-h-[calc(100%-1.5rem)] w-64 overflow-y-auto rounded-lg border border-rule bg-panel/95 p-4 shadow-2xl shadow-black/50 backdrop-blur">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[13px] font-semibold text-slate-100">{t("lm.filters")}</span>
        <Button variant="ghost" size="icon-xs" onClick={onClose} aria-label={t("lm.close")}><FiX /></Button>
      </div>
      <div className="space-y-4">
        <div>
          <span className="eyebrow">{t("lm.layers")}</span>
          <Check on={s.monitored} onChange={(v) => set({ monitored: v })}>{t("lm.layerMonitored")}</Check>
          <Check on={s.manual} onChange={(v) => set({ manual: v })}>{t("lm.layerManual")}</Check>
          <Check on={s.discovered} onChange={(v) => set({ discovered: v })}>{t("lm.layerDiscovered")}</Check>
          <Check on={s.peers} onChange={(v) => set({ peers: v })}>
            {t("lm.layerForeign")}{foreignCount !== null && <span className="data text-slate-500"> ({fmtNum(locale, foreignCount)})</span>}
          </Check>
        </div>
        <div>
          <span className="eyebrow">{t("lm.labels")}</span>
          <Check on={s.names} onChange={(v) => set({ names: v })}>{t("lm.labelNames")}</Check>
          <Check on={s.ips} onChange={(v) => set({ ips: v })}>{t("lm.labelIps")}</Check>
          <Check on={s.ifaces} onChange={(v) => set({ ifaces: v })}>{t("lm.labelIfaces")}</Check>
          <Check on={s.capacity} onChange={(v) => set({ capacity: v })}>{t("lm.labelCapacity")}</Check>
          <Check on={s.rates} onChange={(v) => set({ rates: v })}>{t("lm.labelRates")}</Check>
        </div>
        <div>
          <span className="eyebrow">{t("lm.visualization")}</span>
          <Check on={s.util} onChange={(v) => set({ util: v })}>{t("lm.visUtil")}</Check>
          <Check on={s.flow} onChange={(v) => set({ flow: v })}>{t("lm.visFlow")}</Check>
        </div>
        <div>
          <span className="eyebrow">{t("lm.status")}</span>
          {NODE_STATES.map((st) => (
            <Check key={st} on={!s.hideStatus.includes(st)} onChange={(v) => set({ hideStatus: toggle(s.hideStatus, st, v) })}>
              <span style={{ color: NODE_COLOR[st] }}>●</span> {t(`lm.st.${st}` as Key)}
            </Check>
          ))}
        </div>
        <div>
          <span className="eyebrow">{t("lm.deviceType")}</span>
          {ROLES.map((r) => {
            const I = ROLE_ICON[r];
            return (
              <Check key={r} on={!s.hideRoles.includes(r)} onChange={(v) => set({ hideRoles: toggle(s.hideRoles, r, v) })}>
                <I className="size-3 text-slate-500" /> {t(`lm.role.${r}` as Key)}
              </Check>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Legend({ admin }: { admin: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="absolute bottom-3 left-3 z-10 max-w-[calc(100%-1.5rem)]">
      {open && (
        <div className="mb-2 w-80 space-y-1.5 rounded-lg border border-rule bg-panel/95 p-3 text-[12px] leading-snug text-slate-400 shadow-xl shadow-black/40 backdrop-blur">
          {(["lm.help1", "lm.help2", "lm.help3", "lm.help4", "lm.help5"] as Key[]).map((k) => <p key={k}>{t(k)}</p>)}
          {admin && <p>{t("lm.help6")}</p>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-rule bg-panel/90 px-3 py-1.5 text-[11px] text-slate-400 backdrop-blur">
        {LINK_STATES.map((ls) => (
          <span key={ls} className="inline-flex items-center gap-1.5">
            <svg width={18} height={6} aria-hidden>
              <line x1={0} y1={3} x2={18} y2={3} stroke={LINK_COLOR[ls]} strokeWidth={ls === "high" ? 4 : 2.5} strokeDasharray={ls === "down" ? "3 3" : undefined} />
            </svg>
            {t(`lm.link.${ls}` as Key)}
          </span>
        ))}
        <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-slate-300 hover:text-signal">
          <FiHelpCircle /> {t("lm.help")} <FiChevronDown className={cn("transition-transform", open && "rotate-180")} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function NetMapPage() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [s, set] = useSettings();
  const [coreKeys, setCoreKeys] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(CORES_KEY) ?? "[]"); } catch { return []; }
  });
  useEffect(() => localStorage.setItem(CORES_KEY, JSON.stringify(coreKeys)), [coreKeys]);
  const [dialog, setDialog] = useState<{ edit: NetLink | null } | null>(null);
  const [portsNode, setPortsNode] = useState<NetMapNode | null>(null);
  const [sel, setSel] = useState<Sel>(null);
  const [trace, setTrace] = useState<number | null>(null);
  const [impact, setImpact] = useState<Failure | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const qc = useQueryClient();

  const { data, error, isLoading, isError, dataUpdatedAt } = useQuery<NetMapResponse>({
    queryKey: ["netmap", s.peers],
    queryFn: () => getNetMap(s.peers),
    refetchInterval: REFRESH_MS,
  });
  const overview = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: OVERVIEW_REFRESH_MS });
  const { data: linkData } = useQuery({ queryKey: ["net-links"], queryFn: listNetLinks });
  const { data: posData, isPending: posPending } = useQuery({ queryKey: ["net-positions"], queryFn: listNetPositions });

  // Colliding SNMP names ("olt", "olt") get their address: every label, search hit and panel reads the result.
  const nodes = useMemo(() => disambiguate(data?.nodes ?? []), [data]);
  const allEdges = useMemo(() => data?.edges ?? [], [data]);

  // --- the operational model: every link counts, whatever is drawn.
  const g: Graph = useMemo(() => {
    const adj = adjacency(nodes, allEdges);
    const roles = inferRoles(nodes, adj, new Set(nodes.filter((n) => coreKeys.includes(n.key)).map((n) => n.id)));
    return {
      byId: new Map(nodes.map((n) => [n.id, n])),
      edges: allEdges,
      roles,
      states: new Map(nodes.map((n) => [n.id, nodeState(n)])),
      cores: new Set(nodes.filter((n) => roles.get(n.id) === "core").map((n) => n.id)),
      adj,
      ends: new Map(allEdges.map((e) => [edgeKey(e), [e.source, e.target] as [number, number]])),
      olts: overview.data?.olts ?? [],
      alerts: overview.data?.alerts_open ?? [],
    };
  }, [nodes, allEdges, overview.data, coreKeys]);

  // --- what is drawn: layers and filters.
  const visible = useMemo(
    () => nodes.filter((n) =>
      (n.kind === "neighbour" || s.monitored) &&
      !s.hideStatus.includes(g.states.get(n.id)!) &&
      !s.hideRoles.includes(g.roles.get(n.id)!)),
    [nodes, s.monitored, s.hideStatus, s.hideRoles, g],
  );
  const visibleIds = useMemo(() => new Set(visible.map((n) => n.id)), [visible]);
  // A foreign device's links are all discovered: they come with the foreign
  // layer, so turning it on never shows peers floating unconnected.
  const edges = useMemo(() => {
    const foreign = (id: number) => g.byId.get(id)?.kind === "neighbour";
    return allEdges.filter((e) =>
      (e.origin === "manual" ? s.manual : s.discovered || foreign(e.source) || foreign(e.target)) &&
      visibleIds.has(e.source) && visibleIds.has(e.target));
  }, [allEdges, s.manual, s.discovered, visibleIds, g]);

  // Manual edge -> stored link, matched on the unordered pair of stable node
  // keys (the pair is unique, so this is exact and survives re-mirroring).
  const linkForEdge = useMemo(() => {
    const keyOf = new Map(nodes.map((n) => [n.id, n.key]));
    const byPair = new Map<string, NetLink>();
    for (const l of linkData?.items ?? []) byPair.set([l.source_node, l.target_node].sort().join("|"), l);
    const out = new Map<string, NetLink>();
    for (const e of allEdges) {
      if (e.origin !== "manual") continue;
      const a = keyOf.get(e.source), b = keyOf.get(e.target);
      const l = a && b ? byPair.get([a, b].sort().join("|")) : undefined;
      if (l) out.set(edgeKey(e), l);
    }
    return out;
  }, [nodes, allEdges, linkData]);

  // Operator-dragged positions, keyed on the stable node key. Local overlay so a
  // drag is instant and survives while the PUT is in flight.
  const [localPos, setLocalPos] = useState<Record<string, Point>>({});
  const saved = useMemo(() => {
    const m = new Map<string, Point>();
    for (const q of posData?.positions ?? []) m.set(q.node_key, { x: q.x, y: q.y });
    for (const [k, v] of Object.entries(localPos)) m.set(k, v);
    return m;
  }, [posData, localPos]);
  const pos = useMemo(() => applySaved(layout(nodes, allEdges, g.cores), nodes, saved), [nodes, allEdges, saved, g.cores]);

  // --- path to core and impact: the focus set everything else dims around.
  const path = useMemo(() => (trace === null ? null : pathToCore(trace, g.cores, g.adj, g.ends)), [trace, g]);
  const focus = useMemo(() => {
    if (trace !== null) {
      if (!path) return { nodes: new Set([trace]), edges: new Set<string>() };
      return { nodes: path.nodes, edges: new Set([...path.primary, ...(path.backup ?? [])]) };
    }
    if (impact) {
      const ns = impactOf(impact, g.cores, g.adj);
      if (impact.node !== undefined) ns.add(impact.node);
      const es = new Set(allEdges.filter((e) => ns.has(e.source) && ns.has(e.target)).map(edgeKey));
      if (impact.edge) es.add(impact.edge);
      return { nodes: ns, edges: es, impacted: impactOf(impact, g.cores, g.adj) };
    }
    return null;
  }, [trace, path, impact, g, allEdges]);

  // --- viewport.
  const svgRef = useRef<SVGSVGElement>(null);
  const [view, setView] = useState<Box | null>(null);
  const [size, setSize] = useState({ width: 1200, height: 700 });
  const drag = useRef<{ x: number; y: number; view: Box } | null>(null);
  const moved = useRef(false);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ width: e.contentRect.width, height: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [nodes.length > 0]);

  const fit = useCallback(
    () => setView(fitBox(visible.map((n) => pos.get(n.id)).filter((p): p is Point => !!p))),
    [visible, pos],
  );
  // Refit whenever the SET of drawn devices changes (first load, a layer or
  // filter toggle); a refetch with the same devices, or a drag, keeps the
  // operator's viewport. Waits for positions so a hand-placed map is not
  // fitted to its auto layout first.
  const visibleSig = visible.map((n) => n.id).join(",");
  useEffect(() => {
    if (!posPending && visible.length) fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSig, posPending]);

  const zoom = (f: number) =>
    setView((v) => v && { x: v.x + (v.w * (1 - f)) / 2, y: v.y + (v.h * (1 - f)) / 2, w: v.w * f, h: v.h * f });

  const clearFocus = () => { setTrace(null); setImpact(null); };
  const reset = () => { setSel(null); clearFocus(); fit(); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || dialog || portsNode) return;
      setSel(null); setTrace(null); setImpact(null); setFiltersOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog, portsNode]);

  // React DOM registers `wheel` as a *passive* root listener, so
  // preventDefault() from an onWheel prop is ignored and the page scrolls while
  // zooming. Attach it natively and non-passively instead.
  // The update is self-referential so two wheel events batched before a commit
  // both apply (and both anchor on the box they actually zoom); that also keeps
  // `view` out of the deps, so the listener survives an SVG remount.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const rect = el.getBoundingClientRect();
      const scale = ev.deltaY > 0 ? 1.15 : 1 / 1.15;
      setView((v) => {
        if (!v) return v;
        const anchor = clientToUser(v, rect, ev.clientX, ev.clientY);
        return {
          x: anchor.x - (anchor.x - v.x) * scale,
          y: anchor.y - (anchor.y - v.y) * scale,
          w: v.w * scale,
          h: v.h * scale,
        };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [nodes.length]);

  // Pointer events, not mouse events: same code works for mouse, pen and touch
  // (the canvas sets touch-action:none, so without this a touch drag would
  // neither pan the map nor scroll the page). Pointer capture keeps a drag
  // alive when the cursor leaves the SVG.
  const onPointerDown = (e: React.PointerEvent) => {
    // isPrimary: every touch contact reports button 0, so without it a second
    // finger would overwrite the drag origin and make the map jump.
    if (!e.isPrimary || e.button !== 0 || !view) return;
    drag.current = { x: e.clientX, y: e.clientY, view };
    moved.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    // Node drag first: it is driven from here, not from the node <g>, so a fast
    // flick that leaves the node box before the slop threshold still moves it.
    if (nodeDrag.current) return onNodePointerMove(e);
    const d = drag.current;
    if (!d || !svgRef.current) return;
    const dxPx = e.clientX - d.x, dyPx = e.clientY - d.y;
    // Capture only once this is a real drag: capturing on pointerdown retargets
    // the compatibility mouse events to the <svg>, which swallows the node click.
    if (!moved.current && (Math.abs(dxPx) > CLICK_SLOP || Math.abs(dyPx) > CLICK_SLOP)) {
      moved.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const delta = pixelsToUser(d.view, svgRef.current.getBoundingClientRect(), dxPx, dyPx);
    setView({ ...d.view, x: d.view.x - delta.x, y: d.view.y - delta.y });
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (nodeDrag.current) onNodePointerUp(e);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    drag.current = null;
    nodeDrag.current = null;
  };

  // --- node drag (admin): drop a device, it stays. A node pointerdown stops
  // propagation so the canvas pan handler never sees it.
  const nodeDrag = useRef<{ key: string; origin: Point; x: number; y: number; view: Box } | null>(null);
  const pending = useRef<Record<string, Point>>({});
  const saveTimer = useRef<number | null>(null);

  const savePos = useMutation({
    mutationFn: (ps: NetPosition[]) => saveNetPositions(ps),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["net-positions"] }),
    // Nothing was stored, so drop the optimistic overlay: the node snaps back
    // rather than looking placed until the next reload loses it.
    onError: (_err, ps) =>
      setLocalPos((m) => {
        const next = { ...m };
        for (const p of ps) delete next[p.node_key];
        return next;
      }),
  });
  const autoArrange = useMutation({
    mutationFn: clearNetPositions,
    onSuccess: () => {
      setLocalPos({});
      qc.invalidateQueries({ queryKey: ["net-positions"] });
    },
  });
  // Auto arrange changes every position but not the device set: refit by hand.
  useEffect(() => { if (autoArrange.isSuccess && !posPending) fit(); }, [autoArrange.isSuccess, posPending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced so a burst of drags is one request, and one drag is one request.
  const queueSave = (key: string, p: Point) => {
    pending.current[key] = p;
    if (saveTimer.current !== null) clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      const ps = Object.entries(pending.current).map(([node_key, v]) => ({ node_key, x: v.x, y: v.y }));
      pending.current = {};
      if (ps.length) savePos.mutate(ps);
    }, 400);
  };
  useEffect(() => () => { if (saveTimer.current !== null) clearTimeout(saveTimer.current); }, []);

  const nodeDelta = (d: { x: number; y: number; view: Box }, e: React.PointerEvent) =>
    pixelsToUser(d.view, svgRef.current!.getBoundingClientRect(), e.clientX - d.x, e.clientY - d.y);

  const onNodePointerDown = (n: NetMapNode, p: Point) => (e: React.PointerEvent) => {
    if (!e.isPrimary || e.button !== 0 || !view) return;
    e.stopPropagation();
    moved.current = false;
    if (admin) nodeDrag.current = { key: n.key, origin: p, x: e.clientX, y: e.clientY, view };
  };
  const onNodePointerMove = (e: React.PointerEvent) => {
    const d = nodeDrag.current;
    if (!d || !svgRef.current) return;
    const dxPx = e.clientX - d.x, dyPx = e.clientY - d.y;
    if (!moved.current) {
      if (Math.abs(dxPx) <= CLICK_SLOP && Math.abs(dyPx) <= CLICK_SLOP) return;
      moved.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const delta = nodeDelta(d, e);
    setLocalPos((m) => ({ ...m, [d.key]: { x: d.origin.x + delta.x, y: d.origin.y + delta.y } }));
  };
  const onNodePointerUp = (e: React.PointerEvent) => {
    const d = nodeDrag.current;
    nodeDrag.current = null;
    if (!d || !moved.current || !svgRef.current) return;
    const delta = nodeDelta(d, e);
    queueSave(d.key, { x: d.origin.x + delta.x, y: d.origin.y + delta.y });
  };

  // Selection. A click that did not move selects; a drag never does.
  const pickNode = (id: number) => { setSel({ kind: "node", id }); setImpact(null); if (trace !== id) setTrace(null); };
  const pickEdge = (key: string) => { setSel({ kind: "edge", key }); setImpact(null); setTrace(null); };
  const centreOn = (n: NetMapNode) => {
    const p = pos.get(n.id);
    if (p) setView(focusBox(p));
    pickNode(n.id);
  };

  // Arriving from Topology: ?focus=<node key> centres and selects that node.
  // Declared after the first-load refit so it wins that commit.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const key = params.get("focus");
    if (!key || posPending || !nodes.length) return;
    const oltId = key.startsWith("olt:") ? Number(key.slice(4)) : null;
    const n = nodes.find((x) => x.key === key || (oltId !== null && x.olt_id === oltId));
    if (n) centreOn(n);
    const next = new URLSearchParams(params);
    next.delete("focus");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, posPending, nodes.length]);

  const box = view ?? { x: -400, y: -300, w: 800, h: 600 };
  const scale = userScale(box, { left: 0, top: 0, ...size });
  const k = 1 / scale; // user units per screen pixel
  const lod = lodOf(scale);
  const stale = isError;

  const selNode = sel?.kind === "node" ? g.byId.get(sel.id) ?? null : null;
  const selEdge = sel?.kind === "edge" ? allEdges.find((e) => edgeKey(e) === sel.key) ?? null : null;
  const devicesDown = nodes.filter((n) => g.states.get(n.id) === "down").length;
  const linksDown = edges.filter((e) => e.state === "down").length;
  const upCount = nodes.filter((n) => g.states.get(n.id) === "up" || g.states.get(n.id) === "warn").length;
  const foreignCount = data?.counts.neighbours ?? null;

  // Pre-computed geometry so labels can render in their own layer above lines.
  const drawn = edges.flatMap((e) => {
    const a = pos.get(e.source), b = pos.get(e.target);
    return a && b && e.source !== e.target ? [{ e, a, b, key: edgeKey(e), ls: linkState(e) }] : [];
  });
  const dimEdge = (key: string) => !!focus && !focus.edges.has(key);
  const dimNode = (id: number) => !!focus && !focus.nodes.has(id);

  // Far zoom labels the skeleton only, most important first, and drops any
  // label that would land on one already placed (greedy, screen space).
  // ponytail: O(n²) overlap test, fine to a few hundred labels; grid-bucket it beyond.
  const farLabels = (() => {
    if (lod !== "far") return [];
    const rank = (n: NetMapNode) => {
      const r = g.roles.get(n.id);
      if (sel?.kind === "node" && sel.id === n.id) return 0;
      if (r === "core") return 1;
      if (g.states.get(n.id) === "down") return 2;
      if (focus?.nodes.has(n.id)) return 3;
      return r === "agg" ? 4 : r === "olt" ? 5 : 9;
    };
    const placed: { x: number; y: number; w: number }[] = [];
    const out: NetMapNode[] = [];
    for (const n of [...visible].sort((a, b) => rank(a) - rank(b))) {
      const p = pos.get(n.id);
      if (!p || rank(n) === 9 || dimNode(n.id)) continue;
      const x = p.x * scale, y = (p.y + NODE_H / 2) * scale + 12, w = Math.min(22, n.name.length) * 7.2;
      if (placed.some((q) => Math.abs(q.x - x) < (q.w + w) / 2 + 6 && Math.abs(q.y - y) < 16)) continue;
      placed.push({ x, y, w });
      out.push(n);
    }
    return out;
  })();

  /**
   * Centre for an interface label: where the link leaves the node box, pushed
   * out by the label's own half-extent along the link so it clears the box.
   */
  const exitPoint = (from: Point, to: Point, label: string): Point => {
    const dx = to.x - from.x, dy = to.y - from.y, len = Math.hypot(dx, dy) || 1;
    const tBox = Math.min(dx ? NODE_W / 2 / Math.abs(dx) : Infinity, dy ? NODE_H / 2 / Math.abs(dy) : Infinity);
    const half = ((label.length * 6) / 2) * (Math.abs(dx) / len) + 7 * (Math.abs(dy) / len);
    const d = Math.min(tBox * len + (half + 6) * k, len / 2 - 8 * k);
    return { x: from.x + (dx / len) * d, y: from.y + (dy / len) * d };
  };

  return (
    <div className="-m-4 flex h-[calc(100dvh-3rem)] flex-col gap-3 p-4 md:-m-8 md:px-6 md:py-5">
      {/* Header: identity + the four numbers that matter + liveness. */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("nav.netmap")}</h1>
          <div className="data mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-slate-400">
            <span><span className="text-slate-100">{fmtNum(locale, nodes.length)}</span> {t("lm.devices")}</span>
            <span><span className="text-signal-good">{fmtNum(locale, upCount)}</span> {t("lm.up")}</span>
            <span><span className="text-slate-100">{fmtNum(locale, edges.length)}</span> {t("lm.links")}</span>
            <span title={t("lm.downTitle", { d: devicesDown, l: linksDown })}>
              <span className={devicesDown + linksDown ? "text-signal-crit" : "text-slate-100"}>
                {fmtNum(locale, devicesDown + linksDown)}
              </span>{" "}
              {t("lm.down")}
            </span>
          </div>
        </div>
        {data && <Live updatedAt={dataUpdatedAt} failed={isError} every={REFRESH_MS} className="border-l-0 pl-0" />}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Search nodes={visible} edges={edges} onPick={centreOn} />
        <Button size="sm" variant={filtersOpen ? "default" : "secondary"} onClick={() => setFiltersOpen(!filtersOpen)}>
          <FiSliders /> {t("lm.filters")}
        </Button>
        {admin && (
          <Button
            size="sm"
            variant="secondary"
            disabled={autoArrange.isPending}
            title={t("netmap.autoArrangeTitle")}
            onClick={() => { if (confirm(t("netmap.autoArrangeConfirm"))) autoArrange.mutate(); }}
          >
            <FiRefreshCw /> {t("netmap.autoArrange")}
          </Button>
        )}
        {admin && (
          <Button size="sm" onClick={() => setDialog({ edit: null })}>
            <FiPlus /> {t("netmap.addLink")}
          </Button>
        )}
      </div>

      {isLoading && <p className="text-sm text-slate-400">{t("common.loading")}</p>}
      {error && <p className="text-sm text-rose-300">{(error as Error).message}</p>}
      {savePos.error && <p className="text-sm text-rose-300">{t("netmap.posNotSaved", { msg: (savePos.error as Error).message })}</p>}
      {autoArrange.error && <p className="text-sm text-rose-300">{t("netmap.autoArrangeFailed", { msg: (autoArrange.error as Error).message })}</p>}

      {data && nodes.length === 0 && (
        <Card>
          <CardContent className="space-y-2 py-6 text-sm text-slate-400">
            <p>{t("netmap.noDevices")}</p>
            <p>{t("netmap.noDevicesHint")}</p>
            <Link className="text-sky-300 hover:underline" to="/integrations">{t("netmap.configureLink")}</Link>
          </CardContent>
        </Card>
      )}
      {data && nodes.length > 0 && allEdges.length === 0 && (
        <Card>
          <CardContent className="space-y-2 py-6 text-sm text-slate-400">
            <p>{t("netmap.noLinks", { n: fmtNum(locale, nodes.length) })}</p>
            <p>
              {t("netmap.noLinksHint")} {t(admin ? "netmap.noLinksAdmin" : "netmap.noLinksViewer")}
              {foreignCount ? t("netmap.peersExist", { n: fmtNum(locale, foreignCount) }) : ""}
            </p>
          </CardContent>
        </Card>
      )}

      {nodes.length > 0 && (
        <div className="relative min-h-[420px] flex-1 overflow-hidden rounded-lg border border-rule console-bg">
          <svg
            ref={svgRef}
            role="application"
            aria-label={t("netmap.svgLabel", { nodes: visible.length, links: edges.length })}
            className="absolute inset-0 h-full w-full cursor-grab touch-none select-none active:cursor-grabbing"
            viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onClick={(e) => { if (e.target === e.currentTarget && !moved.current) { setSel(null); clearFocus(); } }}
          >
            <title>{t("netmap.svgTitle", { nodes: visible.length, links: edges.length })}</title>
            {/* Neighbour names are free text from the far end (long, ALL CAPS),
                so the character budget alone lets them paint past the border. */}
            <defs>
              <clipPath id="netmap-node-label">
                <rect x={0} y={0} width={NODE_W - 8} height={NODE_H} />
              </clipPath>
            </defs>

            {/* Links. Stroke widths are screen pixels (non-scaling), so a
                congested trunk stays visibly thicker at every zoom. */}
            {drawn.map(({ e, a, b, key, ls }) => {
              const color = s.util || ls === "down" ? LINK_COLOR[ls] : C.none;
              const w = ls === "down" ? 2.5 : 1.5 + (Math.min(100, e.util_pct ?? 0) / 100) * 4.5;
              const onPath = path?.primary.includes(key) ? "primary" : path?.backup?.includes(key) ? "backup" : null;
              const selected = sel?.kind === "edge" && sel.key === key;
              const onEdgeClick = () => { if (!moved.current) pickEdge(key); };
              return (
                <g key={key} className="cursor-pointer" opacity={dimEdge(key) ? 0.1 : 1}>
                  {(selected || onPath || impact?.edge === key) && (
                    <line
                      x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke={impact?.edge === key ? C.crit : C.signal}
                      strokeOpacity={0.35}
                      strokeWidth={w + 9}
                      strokeLinecap="round"
                      strokeDasharray={onPath === "backup" ? "10 8" : undefined}
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                  <line
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={color}
                    strokeWidth={w}
                    strokeDasharray={ls === "down" ? "4 5" : e.origin === "discovered" ? "10 6" : undefined}
                    opacity={e.origin === "discovered" ? 0.65 : 0.95}
                    vectorEffect="non-scaling-stroke"
                  />
                  {/* Traffic flow: duration and direction from netflow.ts;
                      "none" drifts back and forth. Down links stay still. */}
                  {s.flow && ls !== "down" && (
                    <line
                      x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      className="netmap-flow"
                      stroke="#e2e8f0"
                      strokeWidth={1.25}
                      strokeDasharray="4 12"
                      opacity={0.45}
                      pointerEvents="none"
                      vectorEffect="non-scaling-stroke"
                      style={{
                        animationDuration: `${flowDuration(e.util_pct)}s`,
                        animationDirection: { forward: "normal", reverse: "reverse", none: "alternate" }[flowDirection(e.rx_bps, e.tx_bps)],
                      }}
                    />
                  )}
                  {/* Fat invisible line so a link is clickable at any zoom. */}
                  <line
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke="transparent" strokeWidth={18} vectorEffect="non-scaling-stroke"
                    onClick={onEdgeClick}
                  >
                    <title>{`${g.byId.get(e.source)?.name} ${e.local_port ?? ""} ↔ ${g.byId.get(e.target)?.name} ${e.remote_port ?? ""} · ${t(`lm.link.${ls}` as Key)} · RX ${formatBps(e.rx_bps)} / TX ${formatBps(e.tx_bps)}`}</title>
                  </line>
                </g>
              );
            })}

            {/* Link labels, screen-constant size. Problems always show; the rest
                appear as the operator zooms in. */}
            {drawn.map(({ e, a, b, key, ls }) => {
              const selected = sel?.kind === "edge" && sel.key === key;
              const problem = ls === "down" || ls === "high";
              const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
              const op = dimEdge(key) ? 0.1 : 1;
              // Interface names are the densest text on the map: only at close zoom.
              const ifaces = scale >= 1.05 && s.ifaces && !dimEdge(key);
              const parts: string[] = [];
              if (lod === "near" || selected) {
                if (s.capacity && e.speed_bps) parts.push(formatBps(e.speed_bps));
                if (s.rates) parts.push(`↓${formatBps(e.rx_bps)} ↑${formatBps(e.tx_bps)}`);
              }
              const showUtil = ls !== "down" && e.util_pct !== null &&
                (problem || selected || lod === "near" || (lod === "mid" && ls === "moderate"));
              if (showUtil) parts.push(pct(e.util_pct));
              const text = parts.join("  ·  ");
              const tw = text.length * 6.4 + 14;
              const pa = ifaces && e.local_port ? exitPoint(a, b, short(e.local_port, 18)) : null;
              const pb = ifaces && e.remote_port ? exitPoint(b, a, short(e.remote_port, 18)) : null;
              return (
                <g key={key} pointerEvents="none" opacity={op}>
                  {ls === "down" ? (
                    <g transform={`translate(${m.x},${m.y}) scale(${k})`}>
                      <circle r={10} fill="#2a0f14" stroke={C.crit} strokeWidth={1.5} />
                      <path d="M-4 -4 L4 4 M4 -4 L-4 4" stroke={C.crit} strokeWidth={2} strokeLinecap="round" />
                      {lod !== "far" && (
                        <text x={15} y={4} fontSize={10.5} fontWeight={600} fill={C.crit} letterSpacing={1} className="data"
                          stroke="#05080f" strokeWidth={3} paintOrder="stroke">
                          {t("lm.link.down").toUpperCase()}
                        </text>
                      )}
                    </g>
                  ) : text ? (
                    <g transform={`translate(${m.x},${m.y}) scale(${k})`}>
                      <rect x={-tw / 2} y={-10} width={tw} height={20} rx={4} fill={C.ink} fillOpacity={0.94}
                        stroke={s.util ? LINK_COLOR[ls] : "#334155"} strokeOpacity={problem ? 0.9 : 0.5} />
                      <text textAnchor="middle" y={4} fontSize={11} className="data"
                        fill={problem && s.util ? LINK_COLOR[ls] : "#cbd5e1"}>{text}</text>
                    </g>
                  ) : null}
                  {[[pa, e.local_port], [pb, e.remote_port]].map(([p, name], i) =>
                    p && name ? (
                      <text key={i} transform={`translate(${(p as Point).x},${(p as Point).y}) scale(${k})`} y={3.5}
                        textAnchor="middle" fontSize={10} className="data" fill="#8391a7"
                        stroke="#05080f" strokeWidth={3} paintOrder="stroke">
                        {short(name as string, 18)}
                      </text>
                    ) : null,
                  )}
                </g>
              );
            })}

            {/* Devices. Role sets weight (size, border, type), status sets the
                rail, icon and word. Foreign devices recede. */}
            {visible.map((n) => {
              const p = pos.get(n.id);
              if (!p) return null;
              const role = g.roles.get(n.id)!;
              const state = g.states.get(n.id)!;
              const shown: Shown = stale && role !== "foreign" && state !== "down" ? "stale" : state;
              const foreign = role === "foreign";
              const major = role === "core" || role === "agg";
              const color = NODE_COLOR[shown];
              const Icon = ROLE_ICON[role];
              const SIcon = STATE_ICON[shown];
              const isSel = sel?.kind === "node" && sel.id === n.id;
              const hit = focus && "impacted" in focus && focus.impacted?.has(n.id);
              const failed = impact?.node === n.id;
              const secondary = s.ips ? n.hostname
                : foreign ? (n.protocol ?? "LLDP/CDP")
                : n.cpu_pct !== null || n.mem_pct !== null ? `cpu ${pct(n.cpu_pct)} · mem ${pct(n.mem_pct)}`
                : n.os ?? "";
              const chip = shown !== "up" && !foreign;
              const chipLabel = t(`lm.st.${shown}` as Key).toUpperCase();
              const chipW = chipLabel.length * 6.2 + 24;
              return (
                <g
                  key={n.id}
                  transform={`translate(${p.x - NODE_W / 2},${p.y - NODE_H / 2})`}
                  opacity={dimNode(n.id) ? 0.14 : foreign ? 0.7 : 1}
                  onPointerDown={onNodePointerDown(n, p)}
                  onClick={() => { if (!moved.current) pickNode(n.id); }}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); pickNode(n.id); }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`${t(`lm.role.${role}` as Key)} ${n.name}: ${t(`lm.st.${shown}` as Key)}`}
                  className={cn("outline-none", admin ? "cursor-move" : "cursor-pointer")}
                >
                  <title>{foreign ? t("lm.foreignNote", { proto: n.protocol ?? "LLDP/CDP" }) : `${n.name} · ${n.hostname}`}</title>
                  {shown === "down" && (
                    <rect x={-5} y={-5} width={NODE_W + 10} height={NODE_H + 10} rx={10} fill="none" stroke={C.crit} strokeOpacity={0.4} strokeWidth={4} />
                  )}
                  {role === "core" && (
                    <rect x={-4} y={-4} width={NODE_W + 8} height={NODE_H + 8} rx={9} fill="none" stroke="#56627a" strokeWidth={1} />
                  )}
                  {(isSel || hit || failed) && (
                    <rect
                      x={-7} y={-7} width={NODE_W + 14} height={NODE_H + 14} rx={11} fill="none"
                      stroke={isSel ? C.signal : failed || state === "down" ? C.crit : C.warn}
                      strokeWidth={2} vectorEffect="non-scaling-stroke"
                      strokeDasharray={!isSel && !failed && state !== "down" ? "5 4" : undefined}
                    />
                  )}
                  <rect
                    width={NODE_W} height={NODE_H} rx={6}
                    fill={shown === "down" ? "#2a0f14" : foreign ? "#05080f" : major ? "#101a2c" : C.ink}
                    stroke={foreign ? "#475569" : shown === "up" ? (major ? "#64748b" : "#2c3a52") : color}
                    strokeWidth={role === "core" ? 2 : major || shown !== "up" ? 1.5 : 1}
                    strokeDasharray={foreign ? "5 4" : undefined}
                  />
                  {!foreign && <rect x={0} y={0} width={4} height={NODE_H} rx={2} fill={color} />}
                  {lod === "near" && (
                    <>
                      <Icon x={12} y={8} size={14} color={foreign ? "#64748b" : major ? "#cbd5e1" : "#8391a7"} />
                      <g clipPath="url(#netmap-node-label)">
                        {s.names && (
                          <text x={32} y={20} fontSize={major ? 13.5 : 13} fontWeight={major ? 600 : 500}
                            fill={foreign ? "#94a3b8" : "#e2e8f0"}>
                            {midTrim(n.name, 16)}
                          </text>
                        )}
                        <text x={12} y={37} fontSize={9.5} className="data" fill="#64748b" letterSpacing={0.8}>
                          {ROLE_TAG[role]}
                          <tspan fill="#8391a7" letterSpacing={0}>{secondary ? `  ${short(secondary, 18)}` : ""}</tspan>
                        </text>
                      </g>
                    </>
                  )}
                  {/* Mid zoom: one line, the name, drawn big enough to read. */}
                  {lod === "mid" && s.names && (() => {
                    const f = Math.min(24, 11 * k);
                    return (
                      <text x={14} y={NODE_H / 2 + f * 0.36} fontSize={f} fontWeight={major ? 600 : 500}
                        fill={foreign ? "#94a3b8" : "#e2e8f0"} clipPath="url(#netmap-node-label)">
                        {midTrim(n.name, Math.floor((NODE_W - 20) / (0.58 * f)))}
                      </text>
                    );
                  })()}
                  {chip && (
                    <g transform={`translate(${NODE_W},0) scale(${lod === "far" ? Math.max(1, k * 0.9) : 1})`}>
                      <rect x={-chipW + 6} y={-9} width={chipW} height={17} rx={3} fill={shown === "down" ? C.crit : C.ink} stroke={color} />
                      <SIcon x={-chipW + 11} y={-5.5} size={10} color={shown === "down" ? "#fff" : color} />
                      <text x={-chipW + 25} y={3} fontSize={9.5} fontWeight={600} letterSpacing={0.8} className="data"
                        fill={shown === "down" ? "#fff" : color}>{chipLabel}</text>
                    </g>
                  )}
                </g>
              );
            })}

            {/* Far zoom: only the skeleton is labelled (core, aggregation, OLT)
                plus anything broken or selected, at readable screen size. */}
            {lod === "far" && s.names && farLabels.map((n) => {
              const p = pos.get(n.id)!;
              const role = g.roles.get(n.id)!;
              return (
                <text key={n.id} transform={`translate(${p.x},${p.y + NODE_H / 2 + 6 * k}) scale(${k})`} y={11}
                  textAnchor="middle" fontSize={role === "core" ? 12.5 : 11.5} fontWeight={role === "core" ? 600 : 500}
                  fill="#e2e8f0" stroke="#05080f" strokeWidth={3} paintOrder="stroke" pointerEvents="none">
                  {midTrim(n.name, 22)}
                </text>
              );
            })}
          </svg>

          {filtersOpen && <FiltersPanel s={s} set={set} foreignCount={foreignCount} onClose={() => setFiltersOpen(false)} />}

          {(trace !== null || impact) && (
            <div className="absolute top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-full border border-rule bg-panel/95 py-1 pr-1 pl-4 text-[12px] text-slate-300 shadow-lg shadow-black/40 backdrop-blur">
              {trace !== null ? (
                <span><span className="eyebrow mr-2 text-signal">{t("lm.pathToCore")}</span>{g.byId.get(trace)?.name}</span>
              ) : (
                <span>
                  <span className="eyebrow mr-2 text-signal-warn">{t("lm.impactPotential")}</span>
                  {t("lm.impactOf", {
                    name: impact!.node !== undefined
                      ? g.byId.get(impact!.node)?.name ?? "?"
                      : (() => { const [a, b] = g.ends.get(impact!.edge!) ?? [0, 0]; return `${g.byId.get(a)?.name} ↔ ${g.byId.get(b)?.name}`; })(),
                  })}
                </span>
              )}
              <Button size="xs" variant="secondary" className="rounded-full" onClick={clearFocus}>{t("lm.clear")}</Button>
            </div>
          )}

          <Legend admin={admin} />

          <div className={cn("absolute bottom-3 z-10 flex flex-col overflow-hidden rounded-md border border-rule bg-panel/90 backdrop-blur transition-[right]", sel ? "right-[calc(min(380px,100%)+0.75rem)]" : "right-3")}>
            {[
              { label: t("lm.zoomIn"), icon: <FiPlus />, on: () => zoom(1 / 1.3) },
              { label: t("lm.zoomOut"), icon: <FiMinus />, on: () => zoom(1.3) },
              { label: t("netmap.fit"), icon: <FiMaximize />, on: fit },
              { label: t("lm.reset"), icon: <FiRotateCcw />, on: reset },
            ].map((b) => (
              <button key={b.label} type="button" onClick={b.on} title={b.label} aria-label={b.label}
                className="grid size-8 place-items-center border-b border-rule text-slate-400 last:border-b-0 hover:bg-white/[0.04] hover:text-slate-100">
                {b.icon}
              </button>
            ))}
          </div>

          {selEdge && (
            <LinkInspector
              edge={selEdge} g={g} link={linkForEdge.get(edgeKey(selEdge)) ?? null} admin={admin}
              impactShown={impact?.edge === edgeKey(selEdge)}
              onImpact={() => { setTrace(null); setImpact(impact?.edge === edgeKey(selEdge) ? null : { edge: edgeKey(selEdge) }); }}
              onPick={pickNode}
              onEdit={(l) => setDialog({ edit: l })}
              onClose={() => { setSel(null); clearFocus(); }}
            />
          )}
          {selNode && (
            <DeviceInspector
              node={selNode} g={g} path={trace === selNode.id ? path : null} tracing={trace === selNode.id}
              impactShown={impact?.node === selNode.id}
              onTrace={() => { setImpact(null); setTrace(trace === selNode.id ? null : selNode.id); }}
              onImpact={() => { setTrace(null); setImpact(impact?.node === selNode.id ? null : { node: selNode.id }); }}
              onPick={pickNode}
              onPickEdge={pickEdge}
              onPorts={() => setPortsNode(selNode)}
              pinned={coreKeys.includes(selNode.key)}
              onPin={() => setCoreKeys((ks) => ks.includes(selNode.key) ? ks.filter((x) => x !== selNode.key) : [...ks, selNode.key])}
              onClose={() => { setSel(null); clearFocus(); }}
            />
          )}
        </div>
      )}

      {portsNode && <DevicePortsDialog node={portsNode} onClose={() => setPortsNode(null)} />}
      {dialog && <LinkDialog edit={dialog.edit} onClose={() => setDialog(null)} />}
    </div>
  );
}
